import { Button, Group, LoadingOverlay, Stack, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect } from 'react';

import { Declarant } from '@e-origin/shared';

import { LeavePageNotification } from '@components';
import { useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { ExcludedList } from './ExcludedList';
import classes from './ManageAccount.module.css';
import { SettingCheckbox } from './SettingCheckbox';

interface YListFormValues {
  YListEnabled: boolean;
  YList: string[];
}

const buildInitialValues = (declarant: Declarant): YListFormValues => ({
  YListEnabled: declarant.validationSettings?.YListEnabled || false,
  YList: declarant.validationSettings?.YList || [],
});

export const YListForm = () => {
  const { data: declarant, isLoading, isPlaceholderData } = useDeclarant();
  const updateDeclarantMutation = useUpdateDeclarant();
  const form = useForm<YListFormValues>({ initialValues: { YListEnabled: false, YList: [] } });

  useEffect(() => {
    if (declarant && !isPlaceholderData) {
      form.initialize(buildInitialValues(declarant));
    }
  }, [declarant, isPlaceholderData]);

  const onSubmit = (values: YListFormValues) => {
    updateDeclarantMutation.mutate(
      {
        declarantId: declarant._id,
        declarant: { validationSettings: { ...declarant.validationSettings, ...values } },
      },
      { onSuccess: () => form.resetDirty(values) },
    );
  };

  if (!form.initialized) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />
      <form onSubmit={form.onSubmit(onSubmit)} className={classes.fillHeight}>
        <Stack gap="md" h="100%">
          <Title order={4} ta="center">
            Additional Reference List
          </Title>
          <SettingCheckbox
            {...form.getInputProps('YListEnabled')}
            label="Additional reference list enabled"
            checked={form.values.YListEnabled}
            description="Generate documents with the additional references provided in the list when creating new declarations"
          />
          {form.values.YListEnabled && (
            <ExcludedList
              key={(declarant.validationSettings?.YList || []).join(',')}
              name="Additional reference"
              inputPlaceholder="Additional reference"
              list={form.values.YList}
              onUpdate={(list: string[]) => form.setFieldValue('YList', list)}
              fill
            />
          )}
          <Group>
            <Button type="submit" variant="filled" size="lg">
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  );
};
